/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'

/**
 * Import async components.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'caviar-concierge': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.caviar-concierge' */'~async/caviar-concierge/caviar-concierge') }),
    'collection-hero': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-hero' */'~async/collection-hero/collection-hero') }),
    'contact-us-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-us-page' */'~async/contact-us-page/contact-us-page') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'faq-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.faq-accordion' */'~async/faq-accordion/faq-accordion') }),
    'free-gift-notification': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.free-gift-notification' */'~async/free-gift-notification/free-gift-notification') }),
    'gift-message': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.gift-message' */'~async/gift-message/gift-message') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-dev': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-dev/main-dev-file') }),
    'main-quiz': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-quiz' */'~async/main-quiz/main-quiz') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'mega-menu': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.mega-menu' */'~async/mega-menu/mega-menu') }),
    'mini-product-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.mini-product-form' */'~async/mini-product-form/mini-product-form') }),
    'newsletter-signup': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.newsletter-signup' */'~async/newsletter-signup/newsletter-signup') }),
    'product-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-carousel' */'~async/product-carousel/product-carousel') }),
    'product-details': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-details' */'~async/product-details/product-details') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'quick-view': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.quick-view' */'~async/quick-view/quick-view') }),
    'shop-the-look': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.shop-the-look' */'~async/shop-the-look/shop-the-look') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'store-locations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.store-locations' */'~async/store-locations/store-locations') }),
    'taste-notes': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.taste-notes' */'~async/taste-notes/taste-notes') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import Navigation from '~global/site-header-group/navigation/navigation'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SearchBar from '~global/search-bar/search-bar'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
import VideoPlayer from '~global/video-player/video-player'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'index': Index,
    'navigation': Navigation,
    'overlay': Overlay,
    'product-card': ProductCard,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'search-bar': SearchBar,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    'video-player': VideoPlayer,
    // canvas-global-object-end
  }
}

/**
 * Import web components.
 * - Must be manually updated with new async web components (components/async).
 * - Use component command import to update.
 */
// canvas-web-import-start
import(/* webpackChunkName: 'web.brand-usp' */'~web/brand-usp/brand-usp')
import(/* webpackChunkName: 'web.caviar-quiz' */'~web/caviar-quiz/caviar-quiz')
import(/* webpackChunkName: 'web.collection-list' */'~web/collection-list/collection-list')
import(/* webpackChunkName: 'web.newsletter-callout' */'~web/newsletter-callout/newsletter-callout')
import(/* webpackChunkName: 'web.non-existent-page' */'~web/non-existent-page/non-existent-page')
import(/* webpackChunkName: 'web.special-categories' */'~web/special-categories/special-categories')
import(/* webpackChunkName: 'web.two-column' */'~web/two-column/two-column')
// canvas-web-import-end

/**
 * Import Vuex stores.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
import QuickViewStore from '~stores/quick-view'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    'quick-view': QuickViewStore,
    // canvas-stores-object-end
  }
}
